'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { FileUploadProgress } from '@/types/uploadProgress';
import { formatSpeed, formatSize } from '@/lib/uploadUtils';

interface FileUploadItemProps {
  file: FileUploadProgress;
  onRetry?: (fileId: string) => void;
}

/**
 * FileUploadItem Component
 * 
 * Displays the upload progress for a single file, including:
 * - File name and status icon
 * - Progress bar
 * - Upload speed (when uploading)
 * - Status text and retry button (on error)
 */
export const FileUploadItem: React.FC<FileUploadItemProps> = ({ file, onRetry }) => {
  return (
    <div className="p-4 bg-white/[0.03] border border-white/[0.08] rounded-xl">
      {/* File Name and Status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon 
            name={file.status === 'completed' ? 'CheckCircleIcon' : 
                  file.status === 'error' ? 'XCircleIcon' : 
                  'DocumentIcon'} 
            className="w-5 h-5 flex-shrink-0"
            style={{
              color:
                file.status === 'completed' ? '#34D399' :
                file.status === 'error' ? '#F87171' :
                '#9CA3AF',
            }}
            variant={file.status === 'completed' ? 'solid' : 'outline'}
          />
          <span className="text-sm text-gray-200 truncate">
            {file.file.name}
          </span>
        </div>
        
        {/* Upload Speed */}
        {file.status === 'uploading' && file.uploadSpeed > 0 && (
          <span className="text-xs text-gray-500 ml-2">
            {formatSpeed(file.uploadSpeed)}
          </span>
        )}
      </div>

      {/* Progress Bar */}
      {file.status !== 'error' && (
        <div className="mb-2">
          <div className="w-full h-2 bg-white/[0.08] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ease-out ${
                file.status === 'completed'
                  ? 'bg-gradient-to-r from-emerald-500 to-green-500'
                  : 'bg-gradient-to-r from-purple-500 to-blue-500'
              }`}
              style={{ width: `${file.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Status Text */}
      <div className="flex items-center justify-between text-xs">
        {file.status === 'uploading' && (
          <>
            <span className="text-gray-500">
              {formatSize(file.bytesUploaded)} / {formatSize(file.totalBytes)}
            </span>
            <span className="text-purple-300 font-semibold">
              {Math.round(file.progress)}%
            </span>
          </>
        )}
        {file.status === 'completed' && (
          <span className="text-emerald-400 font-medium">Upload complete</span>
        )}
        {file.status === 'error' && (
          <div className="flex items-center justify-between w-full">
            <span className="text-red-400">{file.error || 'Upload failed'}</span>
            {onRetry && file.retryCount < 3 && (
              <button
                onClick={() => onRetry(file.id)}
                className="text-purple-300 hover:text-purple-200 hover:underline font-semibold"
              >
                Retry
              </button>
            )}
          </div>
        )}
        {file.status === 'queued' && (
          <span className="text-gray-500">Waiting...</span>
        )}
      </div>
    </div>
  );
};

export default FileUploadItem;
